/**
 * OverrideHistory - Standalone page for viewing all supervisor overrides
 * Provides full filtering, sorting, and export capabilities
 */
import React from 'react';
import { useLanguage } from '../../i18n/LanguageContext';
import { Shield } from 'lucide-react';
import OverrideHistoryTable from './components/OverrideHistoryTable';

interface OverrideHistoryProps {
  embedded?: boolean; // When true, hides page title (used in Reports tab view)
}

const OverrideHistory: React.FC<OverrideHistoryProps> = ({ embedded = false }) => {
  const { t } = useLanguage();

  return (
    <div className={embedded ? 'space-y-6' : 'p-6 max-w-[1600px] mx-auto'}>
      {/* Header - hidden when embedded in Reports page */}
      {!embedded && (
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t('supervisor.overrideHistory') || 'Override History'}
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            {t('supervisor.overrideHistoryDesc') || 'View all supervisor overrides and approvals'}
          </p>
        </div>
      )}

      {/* Main content */}
      <div>
        <OverrideHistoryTable
          showSessionColumn={true}
          showFilters={true}
          compact={false}
        />

        {/* Legend */}
        <div className="mt-6 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
            <Shield className="w-4 h-4" />
            {t('supervisor.overrideTypes') || 'Override Types'}
          </h3>
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                Label Reprint
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400">{t('supervisor.labelReprintDesc') || 'Reprinting a box label'}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300">
                Manual Weight
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400">{t('supervisor.manualWeightDesc') || 'Manual weight entry'}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                Yield Sign-off
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400">{t('supervisor.yieldSignoffDesc') || 'Approving yield variance'}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
                EOD Sign-off
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400">{t('supervisor.eodSignoffDesc') || 'End of day sign-off'}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300">
                Session Completion
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400">{t('supervisor.sessionCompletionDesc') || 'Early session completion'}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OverrideHistory;
